import { useEffect, useRef } from "react";

import { usePrevious } from "@mantine/hooks";
import { UseQueryOptions } from "@tanstack/react-query";
import _ from "lodash";

import { ObjectPoolManifestContext } from "lib/shared/object-pool/manifest";
import { User } from "prisma/cm/client";
import { useAppData } from "services/app-data";
import { useAuthenticatedUserFromReduxStore } from "services/user";
import { trpc } from "utils/trpc";
import { ObjectPoolModel, ObjectPoolModelId, ObjectPoolModelName } from "utils/types";

import { markContextCommitted, markContextPending } from "./contextCommit";
import {
  useBoundQueryClient,
  replaceEntityMapManifestInQueryCache,
  mergeEntityMapManifestInQueryCache,
} from "./queryCache";

/**
 * TRPC isn't well-typed right now because of some code-health issues that
 * exist outside the scope of this work, so in the meantime we supply
 * a really dump placeholder for the where-clause and query-key.
 */
type PlaceholderTrpcWhere = { where: { companyId: User["companyId"] } };
type QueryKeyTuple = [trpcProcedurePath: string, input: PlaceholderTrpcWhere];

export function getObjectPoolTrpcProcedurePath(modelName: ObjectPoolModelName): QueryKeyTuple[0] {
  const trpcProcedurePath = ["object", _.camelCase(modelName), "findMany"].join(".");
  return trpcProcedurePath;
}

export function getObjectPoolQueryKey(
  modelName: ObjectPoolModelName,
  companyId: User["companyId"],
): QueryKeyTuple {
  const trpcProcedurePath = getObjectPoolTrpcProcedurePath(modelName);
  const queryInput = { where: { companyId } };
  return [trpcProcedurePath, queryInput];
}

const retryQueryOps: Pick<UseQueryOptions, "retry" | "retryDelay"> = {
  retry: true,
  retryDelay: (attempt) => {
    // Retry with exponential backoff up to a max.
    const intervalMs = 1000;
    const maxBackoffMs = intervalMs * 30;
    const exponentialBackoffIntervalMs = attempt > 1 ? attempt ** intervalMs : intervalMs;
    return Math.min(exponentialBackoffIntervalMs, maxBackoffMs);
  },
};

export function useGetObjectPoolManifestQuery(
  { context }: { context: ObjectPoolManifestContext },
  useQueryOptions: Omit<UseQueryOptions, "queryKey"> = {},
) {
  const user = useAuthenticatedUserFromReduxStore();
  const initializedRef = useRef(false);
  const companyId = user?.companyId as User["companyId"];
  const boundQueryClient = useBoundQueryClient();
  const { enabled: rawEnabled = true, ...optionsRest } = useQueryOptions;
  const enabled = rawEnabled && Boolean(user && user.companyId);
  const manifestQuery = trpc.object.manifest.findAll.useQuery(
    {
      where: {
        // @ts-expect-error TS2769 - The companyId is asserted non-null above
        companyId,
      },
      models: [],
      context,
    },
    {
      ...optionsRest,
      enabled,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      ...retryQueryOps,
    },
  );

  const entityId: string | number | undefined =
    "entityId" in context ? context.entityId : undefined;
  const prevEntityId = usePrevious<string | number | undefined>(entityId);
  const prevContextType = usePrevious<ObjectPoolManifestContext["type"]>(context.type);

  useEffect(() => {
    if (
      (entityId && prevEntityId !== entityId) ||
      (prevContextType && prevContextType !== context.type)
    ) {
      initializedRef.current = false;
      // Mark new context as pending until the manifest is merged into OP cache
      markContextPending(boundQueryClient, context);
    }
  }, [prevEntityId, entityId, prevContextType, context, boundQueryClient]);

  useEffect(() => {
    // The top level query moves the data into a central location to be
    // shared by all other queries. (This used to be handled in an onSuccess
    // callback that was deprecated in react-query v5, so we manage it with
    // this initializedRef pattern for the time being.)

    if (manifestQuery.data && !initializedRef.current) {
      initializedRef.current = true;
      if (context.type === "global") {
        // @ts-expect-error TS2345
        replaceEntityMapManifestInQueryCache(boundQueryClient, manifestQuery.data);
      } else {
        mergeEntityMapManifestInQueryCache(boundQueryClient, manifestQuery.data);
      }
      // Flip committed only after merge/replace completes
      markContextCommitted(boundQueryClient, context);
    }
  }, [manifestQuery.data, boundQueryClient, context]);

  return manifestQuery;
}

export type ObjectPoolEntityMap<TModelName extends ObjectPoolModelName> = {
  ids: Array<ObjectPoolModelId<TModelName>>;
  entities: {
    [s: string]: ObjectPoolModel<TModelName>;
    // This isn't working right now :/
    // [s: ObjectPoolModel<TModelName>["id"]]: ObjectPoolModel<TModelName>;
  };
  indexes: {
    [s in keyof ObjectPoolModel<TModelName>]?: {
      [v: string | number]: Array<ObjectPoolModelId<TModelName>>;
    };
  };
  isReady: boolean;
  modelName: TModelName;
};

const emptyEntity = { ids: [], entities: {}, isReady: false };

export const emptyEntities = {
  AccessExtension: emptyEntity,
  AccessProfile: emptyEntity,
  AccessProfileMemberRelationship: emptyEntity,
  Benefit: emptyEntity,
  BenefitsPackage: emptyEntity,
  BenefitsPackageAssignment: emptyEntity,
  EquityGrant: emptyEntity,
  Company: emptyEntity,
  CompensationApproval: emptyEntity,
  CompensationApprovalLevel: emptyEntity,
  CustomFields: emptyEntity,
  DataImport: emptyEntity,
  LogicRun: emptyEntity,
  EmailAddress: emptyEntity,
  Family: emptyEntity,
  FamilyGroup: emptyEntity,

  FieldTablePlacementConfig: emptyEntity,
  FieldTextSetting: emptyEntity,
  Integration: emptyEntity,
  Job: emptyEntity,
  Level: emptyEntity,
  Objective: emptyEntity,
  PerformanceAnswer: emptyEntity,
  PerformanceCycle: emptyEntity,
  PerformanceQuestion: emptyEntity,
  PerformanceRating: emptyEntity,
  Proposal: emptyEntity,
  ProposerBudget: emptyEntity,
  Range: emptyEntity,
  RangeSet: emptyEntity,
  RangeUpload: emptyEntity,
  Review: emptyEntity,
  TableSettings: emptyEntity,
  Track: emptyEntity,
  User: emptyEntity,
  Zone: emptyEntity,
};

export function useObjectPoolEntities() {
  // TODO: Ask Scotty about this
  const objectsQuery = useGetObjectPoolManifestQuery({ context: { type: "global" } });
  const appData = useAppData();
  // App data can be ready but empty for users with no company.
  return appData.isReady ? objectsQuery.data ?? emptyEntities : emptyEntities;
}
